import React, { useEffect, useMemo, useState, useRef } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  fetchContacts,
  fetchContactLabels,
  fetchContactLabelsWithSearch,
  fetchContactCount,
  fetchContactsByIds,
} from '~/redux/actions/contacts-actions';
import { fetchContactImports } from '~/redux/actions/contact-import-actions';

// Utils
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';
import ImportContacts from '~/components/Contacts/ImportContactsNew';

// MUI Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

// Images
import { SearchByNameAddressCIcon, SearchByImportCIcon, SearchByTagsCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const addContactsContent = [
  {
    id: 'search-by-name-or-address',
    title: 'Search by Name or Address',
    description:
      'Find individual contacts by searching for their name or address (25 contacts max).',
    icon: SearchByNameAddressCIcon,
  },
  // {
  //   id: 'search-by-import-list',
  //   title: 'Search by Import List',
  //   description: 'Select contacts from a list you have previously imported.',
  //   icon: SearchByImportCIcon,
  // },
  {
    id: 'search-by-tag',
    title: 'Search by Tag',
    description: 'Filter your contacts based on tags you have assigned to them.',
    icon: SearchByTagsCIcon,
  },
];

const AddContacts = ({
  view,
  onFileSelectedChange,
  processFileSignal,
  onSubStepChange,
  onProgressStateChange,
  triggerProgressImportSignal,
  triggerProgressBackSignal,
  parentBackSignal,
  onContactsSelectionChange,
  onContactsCountChange,
  // Persisted state from parent
  selectedContacts: propSelectedContacts,
  setSelectedContacts: propSetSelectedContacts,
  selectedImportLists: propSelectedImportLists,
  setSelectedImportLists: propSetSelectedImportLists,
  selectedTags: propSelectedTags,
  setSelectedTags: propSetSelectedTags,
  tagsContactCounts: propTagsContactCounts,
  setTagsContactCounts: propSetTagsContactCounts,
  importSelectedFile,
  setImportSelectedFile,
  importTagsData,
  setImportTagsData,
  // Persisted caches from parent (optional)
  selectedContactsCache: propSelectedContactsCache,
  setSelectedContactsCache: propSetSelectedContactsCache,
  selectedTagsCache: propSelectedTagsCache,
  setSelectedTagsCache: propSetSelectedTagsCache,
  selectedImportListsCache: propSelectedImportListsCache,
  setSelectedImportListsCache: propSetSelectedImportListsCache,
  preSelectedContactIds = [], // Pre-selected contacts from the Contacts page (may be from any page / not in first 20)
}) => {
  const dispatch = useDispatch();

  // Use props if provided, otherwise use local state (for backward compatibility)
  const [localSelectedContacts, setLocalSelectedContacts] = useState([]);
  const [localSelectedImportLists, setLocalSelectedImportLists] = useState([]);
  const [localSelectedTags, setLocalSelectedTags] = useState([]);
  const [localTagsContactCounts, setLocalTagsContactCounts] = useState({});
  const [localSelectedContactsCache, setLocalSelectedContactsCache] = useState({});
  const [localSelectedTagsCache, setLocalSelectedTagsCache] = useState({});
  const [localSelectedImportListsCache, setLocalSelectedImportListsCache] = useState({});

  // Search state for contacts (server-side search) and tags (server-side search)
  const [contactSearch, setContactSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const isInitialSearchRef = useRef(true);
  const isInitialTagSearchRef = useRef(true);
  const preSelectedContactIdsRef = useRef(preSelectedContactIds);  // Capture pre-selected IDs once on mount; stable ref avoids effect re-runs.

  // Cache for selected items' full data to preserve names/labels even when not in current store
  // Use parent-provided caches if available, otherwise fall back to local state
  const selectedContactsCache =
    propSelectedContactsCache !== undefined
      ? propSelectedContactsCache
      : localSelectedContactsCache; // Map<id, contactObject>
  const setSelectedContactsCache = propSetSelectedContactsCache || setLocalSelectedContactsCache;
  const selectedTagsCache =
    propSelectedTagsCache !== undefined ? propSelectedTagsCache : localSelectedTagsCache; // Map<id, tagObject>
  const setSelectedTagsCache = propSetSelectedTagsCache || setLocalSelectedTagsCache;
  const selectedImportListsCache =
    propSelectedImportListsCache !== undefined
      ? propSelectedImportListsCache
      : localSelectedImportListsCache; // Map<id, importLogObject>
  const setSelectedImportListsCache =
    propSetSelectedImportListsCache || setLocalSelectedImportListsCache;

  // Use parent state if provided, otherwise fall back to local state
  const selectedContacts =
    propSelectedContacts !== undefined ? propSelectedContacts : localSelectedContacts;
  const setSelectedContacts = propSetSelectedContacts || setLocalSelectedContacts;
  const selectedImportLists =
    propSelectedImportLists !== undefined ? propSelectedImportLists : localSelectedImportLists;
  const setSelectedImportLists = propSetSelectedImportLists || setLocalSelectedImportLists;
  const selectedTags = propSelectedTags !== undefined ? propSelectedTags : localSelectedTags;
  const setSelectedTags = propSetSelectedTags || setLocalSelectedTags;
  const tagsContactCounts =
    propTagsContactCounts !== undefined ? propTagsContactCounts : localTagsContactCounts;
  const setTagsContactCounts = propSetTagsContactCounts || setLocalTagsContactCounts;

  // Raw data from store
  const contacts = useSelector((state) => state.contactsReducers.contacts) || [];
  const tags = useSelector((state) => state.contactsReducers.labels) || [];
  const importLogs = useSelector((state) => state.contactImportsReducers.contactImports) || [];

  // Loading states from Redux store
  const contactsLoading = useSelector((state) => state.contactsReducers.loading) || false;

  // Local loading state for tags
  const [tagsLoading, setTagsLoading] = useState(false);
  // Track whether the contacts dropdown is currently open
  const [isContactsDropdownOpen, setIsContactsDropdownOpen] = useState(false);

  // Options mapping as { id, title, object }
  const contactOptions = useMemo(() => {
    const mappedContacts = contacts.map((contact) => {
      const namePart =
        contact?.fullName && contact.fullName !== 'null' ? `${contact.fullName}, ` : '';
      const addressPart =
        `${contact?.address1 || ''} ${contact?.address2 || ''} ${contact?.city || ''}, ${contact?.state || ''} ${contact?.zip || ''}`.trim();
      return {
        id: contact.id,
        title: `${namePart}${addressPart}`.replace(/\s+/g, ' ').trim(),
        object: contact,
      };
    });

    // Ensure selected (and, while dropdown is open, recently-selected) contacts are in the options,
    // even if not in current fetched results
    const existingContactIdsSet = new Set(mappedContacts.map((c) => String(c.id)));

    // Build the set of ids we must keep visible:
    // - Always include currently selected contacts
    // - While the contacts dropdown is open, also include any contacts we have in cache.
    const idsToEnsureVisible = new Set(selectedContacts.map((id) => String(id)));
    if (isContactsDropdownOpen) {
      Object.keys(selectedContactsCache || {}).forEach((id) => {
        idsToEnsureVisible.add(String(id));
      });
    }

    // Find contacts that are not in the mapped contacts but we still want visible
    const missingSelectedIds = Array.from(idsToEnsureVisible).filter(
      (id) => !existingContactIdsSet.has(String(id))
    );

    // Try to find missing selected contacts - check cache first, then current contacts array
    const missingOptions = missingSelectedIds.map((id) => {
      // First check cache
      const cachedContact = selectedContactsCache[String(id)];
      if (cachedContact) {
        const namePart =
          cachedContact?.fullName && cachedContact.fullName !== 'null'
            ? `${cachedContact.fullName}, `
            : '';
        const addressPart =
          `${cachedContact?.address1 || ''} ${cachedContact?.address2 || ''} ${cachedContact?.city || ''}, ${cachedContact?.state || ''} ${cachedContact?.zip || ''}`.trim();
        return {
          id: cachedContact.id,
          title: `${namePart}${addressPart}`.replace(/\s+/g, ' ').trim(),
          object: cachedContact,
        };
      }

      // Try to find in full contacts array
      const foundContact = contacts.find((c) => String(c.id) === String(id));
      if (foundContact) {
        const namePart =
          foundContact?.fullName && foundContact.fullName !== 'null'
            ? `${foundContact.fullName}, `
            : '';
        const addressPart =
          `${foundContact?.address1 || ''} ${foundContact?.address2 || ''} ${foundContact?.city || ''}, ${foundContact?.state || ''} ${foundContact?.zip || ''}`.trim();
        return {
          id: foundContact.id,
          title: `${namePart}${addressPart}`.replace(/\s+/g, ' ').trim(),
          object: foundContact,
        };
      }
      // Create placeholder if not found
      return {
        id,
        title: `Contact #${id}`,
        object: { id },
      };
    });

    return [...mappedContacts, ...missingOptions];
  }, [contacts, selectedContacts, selectedContactsCache, isContactsDropdownOpen]);

  const importListOptions = useMemo(() => {
    const mappedLogs = importLogs.map((log) => ({
      id: log.id,
      title: log?.fileName || `Import #${log.id}`,
      object: log,
    }));

    // Ensure all selected import lists are in the options
    const selectedImportListIdsSet = new Set(selectedImportLists.map(String));
    const existingImportListIdsSet = new Set(mappedLogs.map((l) => String(l.id)));

    // Find selected import lists that are not in the mapped logs
    const missingSelectedIds = selectedImportLists.filter(
      (selectedId) => !existingImportListIdsSet.has(String(selectedId))
    );

    // Try to find missing selected import lists - check cache first, then current import logs
    const missingOptions = missingSelectedIds.map((id) => {
      // First check cache
      const cachedImportList = selectedImportListsCache[String(id)];
      if (cachedImportList) {
        return {
          id: cachedImportList.id,
          title: cachedImportList?.fileName || `Import #${cachedImportList.id}`,
          object: cachedImportList,
        };
      }

      // Try to find in full import logs array
      const foundImportList = importLogs.find((log) => String(log.id) === String(id));
      if (foundImportList) {
        return {
          id: foundImportList.id,
          title: foundImportList?.fileName || `Import #${foundImportList.id}`,
          object: foundImportList,
        };
      }

      // Create placeholder if not found
      return {
        id,
        title: `Import List #${id}`,
        object: { id },
      };
    });

    return [...mappedLogs, ...missingOptions];
  }, [importLogs, selectedImportLists, selectedImportListsCache]);

  const tagOptions = useMemo(() => {
    // Show all tags (loaded once on mount), keep alphabetical order
    const sorted = [...tags].sort((a, b) => a?.title?.localeCompare(b?.title));
    const mappedTags = sorted.map((tag) => ({
      id: tag.id,
      title: tag.title,
      object: tag,
    }));

    // Ensure all selected tags are in the options, even if not in current filtered results
    const selectedTagIdsSet = new Set(selectedTags.map(String));
    const existingTagIdsSet = new Set(mappedTags.map((t) => String(t.id)));

    // Find selected tags that are not in the mapped tags
    const missingSelectedIds = selectedTags.filter(
      (selectedId) => !existingTagIdsSet.has(String(selectedId))
    );

    // Try to find missing selected tags - check cache first, then full tags array
    const missingOptions = missingSelectedIds.map((id) => {
      // First check cache
      const cachedTag = selectedTagsCache[String(id)];
      if (cachedTag) {
        return {
          id: cachedTag.id,
          title: cachedTag.title,
          object: cachedTag,
        };
      }

      // Try to find in full tags array
      const foundTag = tags.find((tag) => String(tag.id) === String(id));
      if (foundTag) {
        return {
          id: foundTag.id,
          title: foundTag.title,
          object: foundTag,
        };
      }

      // Create placeholder if not found
      return {
        id,
        title: `Tag #${id}`,
        object: { id, title: `Tag #${id}` },
      };
    });

    // Combine mapped tags with missing selected tags
    // MultiSelect will handle sorting and separating selected/unselected items
    return [...mappedTags, ...missingOptions];
  }, [tags, tagSearch, selectedTags, selectedTagsCache]);

  // Populate cache for selected contacts from current contacts array
  useEffect(() => {
    if (selectedContacts.length === 0) return;

    setSelectedContactsCache((prevCache) => {
      const newCache = { ...prevCache };
      let updated = false;

      selectedContacts.forEach((contactId) => {
        const idStr = String(contactId);
        // Only update cache if we don't have it or if we found a better match in current contacts
        if (!newCache[idStr] || newCache[idStr].id !== contactId) {
          const foundContact = contacts.find((c) => String(c.id) === idStr);
          if (foundContact) {
            newCache[idStr] = foundContact;
            updated = true;
          }
        }
      });

      return updated ? newCache : prevCache;
    });
  }, [contacts, selectedContacts]);

  // Populate cache for selected tags from current tags array
  useEffect(() => {
    if (selectedTags.length === 0) return;

    setSelectedTagsCache((prevCache) => {
      const newCache = { ...prevCache };
      let updated = false;

      selectedTags.forEach((tagId) => {
        const idStr = String(tagId);
        // Only update cache if we don't have it or if we found a better match in current tags
        if (!newCache[idStr] || newCache[idStr].id !== tagId) {
          const foundTag = tags.find((t) => String(t.id) === idStr);
          if (foundTag) {
            newCache[idStr] = foundTag;
            updated = true;
          }
        }
      });

      return updated ? newCache : prevCache;
    });
  }, [tags, selectedTags]);

  // Populate cache for selected import lists from current import logs array
  useEffect(() => {
    if (selectedImportLists.length === 0) return;

    setSelectedImportListsCache((prevCache) => {
      const newCache = { ...prevCache };
      let updated = false;

      selectedImportLists.forEach((importListId) => {
        const idStr = String(importListId);
        // Only update cache if we don't have it or if we found a better match in current import logs
        if (!newCache[idStr] || newCache[idStr].id !== importListId) {
          const foundImportList = importLogs.find((log) => String(log.id) === idStr);
          if (foundImportList) {
            newCache[idStr] = foundImportList;
            updated = true;
          }
        }
      });

      return updated ? newCache : prevCache;
    });
  }, [importLogs, selectedImportLists]);

  // Clean up contacts cache when items are deselected, but only after the contacts dropdown closes
  useEffect(() => {
    if (isContactsDropdownOpen) return;

    setSelectedContactsCache((prevCache) => {
      const selectedIdsSet = new Set(selectedContacts.map(String));
      const cleanedCache = {};
      let updated = false;

      Object.keys(prevCache).forEach((id) => {
        if (selectedIdsSet.has(id)) {
          cleanedCache[id] = prevCache[id];
        } else {
          updated = true;
        }
      });

      return updated ? cleanedCache : prevCache;
    });
  }, [selectedContacts, isContactsDropdownOpen]);

  useEffect(() => {
    setSelectedTagsCache((prevCache) => {
      const selectedIdsSet = new Set(selectedTags.map(String));
      const cleanedCache = {};
      let updated = false;

      Object.keys(prevCache).forEach((id) => {
        if (selectedIdsSet.has(id)) {
          cleanedCache[id] = prevCache[id];
        } else {
          updated = true;
        }
      });

      return updated ? cleanedCache : prevCache;
    });
  }, [selectedTags]);

  useEffect(() => {
    setSelectedImportListsCache((prevCache) => {
      const selectedIdsSet = new Set(selectedImportLists.map(String));
      const cleanedCache = {};
      let updated = false;

      Object.keys(prevCache).forEach((id) => {
        if (selectedIdsSet.has(id)) {
          cleanedCache[id] = prevCache[id];
        } else {
          updated = true;
        }
      });

      return updated ? cleanedCache : prevCache;
    });
  }, [selectedImportLists]);

  // Calculate if any contacts are selected
  const hasContactsSelection = useMemo(() => {
    return selectedContacts.length > 0 || selectedImportLists.length > 0 || selectedTags.length > 0;
  }, [selectedContacts, selectedImportLists, selectedTags]);

  // Calculate total contact count
  const totalContactsCount = useMemo(() => {
    let count = 0;

    // Direct contacts count
    count += selectedContacts.length;

    // Import lists count - sum totalContacts from selected import logs
    selectedImportLists.forEach((importListId) => {
      const importLog = importLogs.find((log) => log.id === importListId);
      if (importLog && importLog.totalContacts) {
        count += importLog.totalContacts;
      }
    });

    // Tags count - sum from tagsContactCounts
    selectedTags.forEach((tagId) => {
      if (tagsContactCounts[tagId]) {
        count += tagsContactCounts[tagId];
      }
    });

    return count;
  }, [selectedContacts, selectedImportLists, selectedTags, importLogs, tagsContactCounts]);

  // Fetch contact count for tags when they're selected
  useEffect(() => {
    const fetchTagCounts = async () => {
      const newCounts = { ...tagsContactCounts };
      let hasUpdates = false;

      for (const tagId of selectedTags) {
        if (!newCounts[tagId]) {
          try {
            const response = await dispatch(fetchContactCount({ tag: tagId }));
            if (response?.data?.data?.contactsCount !== undefined) {
              const count = response.data.data.contactsCount;
              newCounts[tagId] = count;
              hasUpdates = true;
            }
          } catch (error) {}
        }
      }

      if (hasUpdates) {
        setTagsContactCounts(newCounts);
      }
    };

    if (selectedTags.length > 0 && view === 'add_contacts') {
      fetchTagCounts();
    }
  }, [selectedTags, dispatch, view]);

  // Notify parent when selection changes
  useEffect(() => {
    if (onContactsSelectionChange && view === 'add_contacts') {
      onContactsSelectionChange(hasContactsSelection);
    }
  }, [hasContactsSelection, onContactsSelectionChange, view]);

  // Notify parent when contact count changes
  useEffect(() => {
    if (onContactsCountChange && view === 'add_contacts') {
      onContactsCountChange(totalContactsCount);
    }
  }, [totalContactsCount, onContactsCountChange, view]);

  // Debounced function to fetch contacts with search (replicates CreateOrder behavior)
  const debouncedFetchContacts = useMemo(
    () =>
      debounce((searchTerm) => {
        const trimmedSearch =
          searchTerm && searchTerm.length
            ? searchTerm.replace(/^(\S+\s+\S+)\s*(.*)/, '$1').trim()
            : null;

        dispatch(
          fetchContacts({
            pagination: DEFAULT_PAGINATION,
            search: trimmedSearch,
            isReturnAddress: false,
            excludeDncList: true,
          })
        );
      }, 300),
    [dispatch]
  );

  // Call contacts API whenever the user types in the contacts search field
  useEffect(() => {
    if (view !== 'add_contacts') return;

    // Skip the initial run; initial data is fetched on mount
    if (isInitialSearchRef.current) {
      isInitialSearchRef.current = false;
      return;
    }

    debouncedFetchContacts(contactSearch);
  }, [contactSearch, debouncedFetchContacts, view]);

  // Debounced function to fetch tags with search
  const debouncedFetchTags = useMemo(
    () =>
      debounce(async (searchTerm) => {
        setTagsLoading(true);
        try {
          // If search is cleared, fetch only 20 tags again
          // If search exists, fetch with search parameter (no limit or higher limit)
          await dispatch(
            fetchContactLabelsWithSearch({
              pageSize: searchTerm && searchTerm.trim() ? 500 : 20,
              search: searchTerm && searchTerm.trim() ? searchTerm.trim() : null,
            })
          );
        } finally {
          setTagsLoading(false);
        }
      }, 300),
    [dispatch]
  );

  // Fetch tags when search changes (debounced)
  useEffect(() => {
    if (view !== 'add_contacts') return;

    if (isInitialTagSearchRef.current) {
      isInitialTagSearchRef.current = false;
      return;
    }

    debouncedFetchTags(tagSearch);
  }, [tagSearch, debouncedFetchTags, view]);

  // Fetch data on mount.
  // Two parallel calls when pre-selected contacts exist:
  //   1. Regular first-20 fetch → populates the dropdown
  //   2. IDs-based fetch → hydrates selectedContactsCache with full contact objects
  //      so pre-selected contacts from any page render with correct labels.
  useEffect(() => {
    const regularFetch = dispatch(
      fetchContacts({
        pagination: DEFAULT_PAGINATION,
        isReturnAddress: false,
        excludeDncList: true,
      })
    );

    const ids = preSelectedContactIdsRef.current
      .map((c) => Number(c.id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (ids.length > 0) {
      Promise.all([regularFetch, dispatch(fetchContactsByIds(ids))]).then(
        ([, idsResponse]) => {
          const rows = idsResponse?.data?.data?.rows;
          if (Array.isArray(rows) && rows.length > 0) {
            setSelectedContactsCache((prev) => {
              const updated = { ...prev };
              rows.forEach((contact) => {
                updated[String(contact.id)] = contact;
              });
              return updated;
            });
          }
        }
      );
    }

    dispatch(fetchContactLabels());
    dispatch(fetchContactImports(DEFAULT_PAGINATION));
  }, [dispatch]);

  // Reset search state when dropdown closes to show initial options when reopened
  useEffect(() => {
    const openMenus = new Map(); // Track open menus: Map<menuElement, searchType>

    const identifyMenuType = (menu) => {
      // Find which MultiSelect this menu belongs to by checking form controls
      const contentItems = document.querySelectorAll('.addContactsContentItem');

      for (const contentItem of contentItems) {
        const wrapper = contentItem.querySelector('[data-multiselect-type]');
        if (!wrapper) continue;

        const multiselectType = wrapper.getAttribute('data-multiselect-type');
        const formControl = wrapper.querySelector('.multiSelectRedesign');

        if (formControl) {
          // Check if menu is positioned near this form control
          const formRect = formControl.getBoundingClientRect();
          const menuRect = menu.getBoundingClientRect();

          if (
            Math.abs(menuRect.left - formRect.left) < 100 &&
            menuRect.top >= formRect.bottom - 20 &&
            menuRect.top <= formRect.bottom + 300
          ) {
            return multiselectType;
          }
        }
      }
      return null;
    };

    const checkMenus = () => {
      const currentMenus = document.querySelectorAll(
        '.MuiPaper-root.selectOptions, .MuiPaper-root.tagSelectOptions'
      );

      // Check for newly opened menus
      currentMenus.forEach((menu) => {
        if (!openMenus.has(menu)) {
          const searchType = identifyMenuType(menu);
          if (searchType === 'contacts' || searchType === 'tags') {
            openMenus.set(menu, searchType);
          }
        }
      });

      // Check for closed menus (previously open but now missing)
      openMenus.forEach((searchType, menu) => {
        if (!document.body.contains(menu)) {
          // Menu was closed/removed
          if (searchType === 'contacts') {
            setContactSearch('');
            setIsContactsDropdownOpen(false);
          } else if (searchType === 'tags') {
            setTagSearch('');
          }
          openMenus.delete(menu);
        }
      });

      // Update contacts dropdown open state
      const hasContactsOpen = Array.from(openMenus.values()).includes('contacts');
      setIsContactsDropdownOpen(hasContactsOpen);
    };

    // Use MutationObserver to detect menu changes
    const observer = new MutationObserver(() => {
      checkMenus();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also check periodically as backup
    const intervalId = setInterval(checkMenus, 200);

    // Initial check
    checkMenus();

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
      openMenus.clear();
    };
  }, []);

  // Inject loader into dropdown menus when loading
  useEffect(() => {
    const loaderId = 'add-contacts-dropdown-loader';

    // Function to find menu type (contacts or tags)
    const findMenuType = (menu) => {
      const menuClasses = menu.classList;
      if (menuClasses.contains('tagSelectOptions')) {
        return 'tags';
      }
      if (menuClasses.contains('selectOptions')) {
        // Check if it's a contacts menu by finding nearby form control
        const contentItems = document.querySelectorAll('.addContactsContentItem');
        for (const contentItem of contentItems) {
          const wrapper = contentItem.querySelector('[data-multiselect-type]');
          if (!wrapper) continue;

          const multiselectType = wrapper.getAttribute('data-multiselect-type');
          const formControl = wrapper.querySelector('.multiSelectRedesign');

          if (
            formControl &&
            (multiselectType === 'nameOrAddress' || multiselectType === 'returnAddress')
          ) {
            const formRect = formControl.getBoundingClientRect();
            const menuRect = menu.getBoundingClientRect();

            if (
              Math.abs(menuRect.left - formRect.left) < 100 &&
              menuRect.top >= formRect.bottom - 20 &&
              menuRect.top <= formRect.bottom + 300
            ) {
              return 'contacts';
            }
          }
        }
      }
      return null;
    };

    // Function to inject or remove loader
    const updateLoaders = () => {
      // Remove all existing loaders
      document.querySelectorAll(`[id="${loaderId}"]`).forEach((loader) => loader.remove());

      // Find all open dropdown menus
      const menus = document.querySelectorAll('.selectOptions, .tagSelectOptions');

      menus.forEach((menu) => {
        const menuType = findMenuType(menu);
        const menuList =
          menu.querySelector('[role="listbox"]') || menu.querySelector('.MuiMenu-list');

        if (!menuList) return;

        let isLoading = false;
        if (menuType === 'contacts' && contactsLoading) {
          isLoading = true;
        } else if (menuType === 'tags' && tagsLoading) {
          isLoading = true;
        }

        if (isLoading) {
          // Create and inject loader
          const loaderWrapper = document.createElement('div');
          loaderWrapper.id = loaderId;
          loaderWrapper.setAttribute('data-menu-type', menuType);
          loaderWrapper.style.position = 'sticky';
          loaderWrapper.style.top = '0';
          loaderWrapper.style.zIndex = '10';
          loaderWrapper.style.backgroundColor = 'white';

          // Create LinearProgress-like element
          const progressBar = document.createElement('div');
          progressBar.style.height = '4px';
          progressBar.style.backgroundColor = '#f0f0f0';
          progressBar.style.position = 'relative';
          progressBar.style.overflow = 'hidden';

          const progressIndicator = document.createElement('div');
          progressIndicator.style.position = 'absolute';
          progressIndicator.style.top = '0';
          progressIndicator.style.left = '0';
          progressIndicator.style.height = '100%';
          progressIndicator.style.width = '40%';
          progressIndicator.style.backgroundColor = '#ED5C2F';
          progressIndicator.style.animation = 'loading-progress 1.4s ease-in-out infinite';

          progressBar.appendChild(progressIndicator);
          loaderWrapper.appendChild(progressBar);

          // Insert loader at the top of the menu list
          if (menuList.firstChild) {
            menuList.insertBefore(loaderWrapper, menuList.firstChild);
          } else {
            menuList.appendChild(loaderWrapper);
          }
        }
      });
    };

    // Check for menus periodically and update loaders
    const intervalId = setInterval(updateLoaders, 100);

    // Also use MutationObserver to detect when menus open/close
    const observer = new MutationObserver(() => {
      updateLoaders();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial update
    updateLoaders();

    return () => {
      clearInterval(intervalId);
      observer.disconnect();
      // Clean up loaders
      document.querySelectorAll(`[id="${loaderId}"]`).forEach((loader) => loader.remove());
    };
  }, [contactsLoading, tagsLoading]);

  // Hide tooltips when scrolling in dropdown menus
  useEffect(() => {
    let scrollTimeout = null;
    let isScrolling = false;

    const hideAllTooltips = () => {
      // Find all tooltip elements and hide them immediately
      const tooltips = document.querySelectorAll('[role="tooltip"]');
      tooltips.forEach((tooltip) => {
        if (tooltip) {
          tooltip.style.display = 'none';
          tooltip.style.opacity = '0';
          tooltip.style.pointerEvents = 'none';
          tooltip.style.visibility = 'hidden';
        }
      });
    };

    const handleScroll = () => {
      // Mark that scrolling is happening
      isScrolling = true;
      hideAllTooltips();

      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // After scrolling stops, reset after a short delay
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
        scrollTimeout = null;
      }, 200);
    };

    // Function to attach scroll listeners to menu containers
    const attachScrollListeners = () => {
      // Find all dropdown menu containers (MUI Select menus)
      const menuContainers = document.querySelectorAll(
        '.MuiPaper-root.selectOptions, .MuiPaper-root.tagSelectOptions'
      );
      menuContainers.forEach((container) => {
        // Find the scrollable list inside
        const scrollableList = container.querySelector('.MuiList-root');
        if (scrollableList && !scrollableList.dataset.scrollListenerAttached) {
          // Mark as having listener attached
          scrollableList.dataset.scrollListenerAttached = 'true';
          // Add scroll listener
          scrollableList.addEventListener('scroll', handleScroll, { passive: true });
          // Also listen to touchmove for mobile scrolling
          scrollableList.addEventListener('touchmove', handleScroll, { passive: true });
        }
      });
    };

    // Observe when dropdown menus open and attach scroll listeners
    const observer = new MutationObserver(() => {
      attachScrollListeners();
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Check immediately for existing menus
    attachScrollListeners();

    // Also set up interval to catch menus that might open later
    const intervalId = setInterval(attachScrollListeners, 500);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      // Clean up scroll listeners
      const allMenuContainers = document.querySelectorAll(
        '.MuiPaper-root.selectOptions, .MuiPaper-root.tagSelectOptions'
      );
      allMenuContainers.forEach((container) => {
        const scrollableList = container.querySelector('.MuiList-root');
        if (scrollableList) {
          scrollableList.removeEventListener('scroll', handleScroll);
          scrollableList.removeEventListener('touchmove', handleScroll);
          delete scrollableList.dataset.scrollListenerAttached;
        }
      });
    };
  }, []);

  return (
    <>
      {view === 'add_contacts' && (
        <>
          <Typography>
            Choose one of the following methods to add contacts to your order.
          </Typography>

          <Box className="addContactsContent">
            {addContactsContent.map((item) => {
              const isNameOrAddress = item.id === 'search-by-name-or-address';
              const isImportList = item.id === 'search-by-import-list';
              const isTags = item.id === 'search-by-tag';

              const options = isNameOrAddress
                ? contactOptions
                : isImportList
                  ? importListOptions
                  : tagOptions;

              const selectedValue = isNameOrAddress
                ? selectedContacts
                : isImportList
                  ? selectedImportLists
                  : selectedTags;

              const setSelectedValue = isNameOrAddress
                ? setSelectedContacts
                : isImportList
                  ? setSelectedImportLists
                  : setSelectedTags;

              const placeholder = isNameOrAddress ? 'Contacts' : isImportList ? 'Lists' : 'Tags';
              const searchFieldPlaceholder = isNameOrAddress
                ? 'by name or address'
                : isImportList
                  ? 'by import list'
                  : 'tag(s)';
              const hasContactsSelection = selectedContacts.length > 0;
              const hasImportListsSelection = selectedImportLists.length > 0;
              const hasTagsSelection = selectedTags.length > 0;
              const hasAnySelection =
                hasContactsSelection || hasImportListsSelection || hasTagsSelection;

              const isDisabled = hasAnySelection && selectedValue.length === 0;

              return (
                <Box className="addContactsContentItem" key={item.id}>
                  <Box className="addContactsContentItemTitle">
                    <img src={item.icon} alt={item.title} />
                    <Typography variant="h6">{item.title}</Typography>
                  </Box>
                  <Typography variant="h4">{item.description}</Typography>

                  <Box
                    data-multiselect-type={
                      isNameOrAddress ? 'contacts' : isTags ? 'tags' : 'import-lists'
                    }
                  >
                    <MultiSelect
                      options={options}
                      selectedValue={selectedValue}
                      setSelectedValue={setSelectedValue}
                      clearable={true}
                      placeHolderText={placeholder}
                      noOptionText="No options found"
                      component="multipleIds"
                      searchAutocomplete
                      // Use "remove" type for all AddContacts flows so the generic
                      // "Create New Tag" helper option is not shown when no results are found
                      type="remove"
                      searchFieldPlaceholderText={searchFieldPlaceholder}
                      disabled={isDisabled}
                      // Hook search handlers: contacts (server-side) and tags (server-side)
                      onSearchChange={
                        isNameOrAddress ? setContactSearch : isTags ? setTagSearch : undefined
                      }
                      searchLoading={
                        isNameOrAddress ? contactsLoading : isTags ? tagsLoading : false
                      }
                      menuPaperClassName="createOrderDropdownMenu"
                      className={selectedValue.length > 0 ? 'activeState' : 'inactiveState'}
                      activeState={true}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
      {view === 'upload_list' && (
        <>
          <ImportContacts
            view={view}
            onFileSelectedChange={onFileSelectedChange}
            processFileSignal={processFileSignal}
            onSubStepChange={onSubStepChange}
            onProgressStateChange={onProgressStateChange}
            triggerProgressImportSignal={triggerProgressImportSignal}
            triggerProgressBackSignal={triggerProgressBackSignal}
            parentBackSignal={parentBackSignal}
            persistedSelectedFile={importSelectedFile}
            setPersistedSelectedFile={setImportSelectedFile}
            persistedTagsData={importTagsData}
            setPersistedTagsData={setImportTagsData}
          />
        </>
      )}
    </>
  );
};

export default AddContacts;
